/**
 * Human-readable game-config text format (2026-07-18 requirement): the
 * moderator exports the current rules + joker deck as plain text, archives
 * it, and imports it back before a later evening. Deliberately a text
 * format a person can write by hand — not JSON.
 *
 *   # Schreibarena — Spielkonfiguration
 *   Zeit pro Autor:in (Minuten): 7
 *   Strafschritt (Sekunden): 15
 *   Jokerdauer Standard (Sekunden): 120
 *   Geplante Runden: 4
 *
 *   # Jokerdeck — eine Zeile pro Karte: Name | Sekunden oder "manuell" | Beschreibung
 *   Nur mit einer Hand | 120 | Es darf nur mit einer Hand getippt werden.
 */

import type { JokerDef, SessionConfig } from './types';

export interface ParsedConfigText {
  config: Partial<SessionConfig>;
  deck: { name: string; desc?: string; durationMs: number | null }[];
}

export function serializeConfigText(
  config: SessionConfig,
  deck: JokerDef[],
  exportedAtIso: string,
): string {
  const lines = [
    '# Schreibarena — Spielkonfiguration',
    `# Exportiert: ${exportedAtIso}`,
    '',
    `Zeit pro Autor:in (Minuten): ${trimNumber(config.startMs / 60_000)}`,
    `Strafschritt (Sekunden): ${trimNumber(config.penaltyStepMs / 1_000)}`,
    `Jokerdauer Standard (Sekunden): ${trimNumber(config.jokerDefaultMs / 1_000)}`,
    `Geplante Runden: ${config.roundsPlanned}`,
    '',
    '# Jokerdeck — eine Zeile pro Karte: Name | Sekunden oder "manuell" | Beschreibung',
  ];
  for (const j of deck) {
    const dur = j.durationMs === null ? 'manuell' : String(Math.round(j.durationMs / 1_000));
    lines.push(`${j.name} | ${dur}${j.desc ? ` | ${j.desc}` : ''}`);
  }
  return lines.join('\n') + '\n';
}

function trimNumber(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/**
 * Forgiving parser: `Schlüssel: Wert` lines for the four rules (key matching
 * is fuzzy), `Name | Dauer | Beschreibung` lines for the deck, `#`/blank
 * lines ignored. All errors are collected and reported with line numbers;
 * an import applies only when the whole text parses.
 */
export function parseConfigText(text: string): { ok: true; value: ParsedConfigText } | { ok: false; errors: string[] } {
  const config: Partial<SessionConfig> = {};
  const deck: ParsedConfigText['deck'] = [];
  const errors: string[] = [];

  text.split(/\r?\n/).forEach((rawLine, idx) => {
    const line = rawLine.trim();
    const no = idx + 1;
    if (!line || line.startsWith('#')) return;

    if (line.includes('|')) {
      const [name = '', dur = '', ...descParts] = line.split('|').map((p) => p.trim());
      if (!name) {
        errors.push(`Zeile ${no}: Jokername fehlt.`);
        return;
      }
      let durationMs: number | null;
      if (dur === '' || /^manuell$/i.test(dur)) {
        durationMs = null;
      } else {
        const s = parseGermanNumber(dur.replace(/\s*s(ek(unden)?)?\.?$/i, ''));
        if (s === null || s <= 0) {
          errors.push(`Zeile ${no}: Dauer von „${name}“ nicht lesbar („${dur}“ — Sekunden oder "manuell").`);
          return;
        }
        durationMs = Math.round(s * 1_000);
      }
      deck.push({ name, durationMs, ...(descParts.length ? { desc: descParts.join(' | ') } : {}) });
      return;
    }

    // Greedy first group: the split happens at the LAST colon, so keys like
    // "Zeit pro Autor:in (Minuten)" keep their inner colon.
    const kv = /^(.+):\s*(.+)$/.exec(line);
    if (!kv) {
      errors.push(`Zeile ${no}: weder „Schlüssel: Wert“ noch Joker-Zeile („Name | Dauer | Text“).`);
      return;
    }
    const key = kv[1]!.toLowerCase();
    const value = parseGermanNumber(kv[2]!.replace(/\s*(min(uten)?|s(ek(unden)?)?)\.?$/i, ''));
    if (value === null) {
      errors.push(`Zeile ${no}: Zahl nicht lesbar („${kv[2]}“).`);
      return;
    }
    if (key.includes('zeit pro autor')) config.startMs = Math.round(value * 60_000);
    else if (key.includes('strafschritt')) config.penaltyStepMs = Math.round(value * 1_000);
    else if (key.includes('jokerdauer')) config.jokerDefaultMs = Math.round(value * 1_000);
    else if (key.includes('runden')) config.roundsPlanned = Math.round(value);
    else errors.push(`Zeile ${no}: unbekannte Einstellung „${kv[1]!.trim()}“.`);
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, value: { config, deck } };
}

function parseGermanNumber(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
